import React, { useState, useEffect } from 'react';
import { Edit, Trash2, Mail, MessageSquare, X, ChevronDown, ChevronUp, FileText } from 'lucide-react';
import ItemizedEstimatesView from './itemizedEstimateView';
import { EstimateProjectCustomer } from './estimateType';
import { Badge } from '../components/ui/badge';
import { formatAddress } from '../lib/global_function';
import { formatCurrency } from '../lib/global_function';

interface EstimatesTableProps {
    estimates: EstimateProjectCustomer[];
    onEdit: (estimateId: string) => void;
    onDelete: (estimateId: string) => void;
    onSendToCustomer: (estimate_id: string) => void;
    onText: (estimate: EstimateProjectCustomer, estimate_id: string) => void;
    onView: (estimateId: string) => void;
}

const EstimateTableView: React.FC<EstimatesTableProps> = ({
    estimates,
    onEdit,
    onDelete,
    onSendToCustomer,
    onText,
    onView,
}) => {
    const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
    const [selectedEstimateId, setSelectedEstimateId] = useState<string | null>(null);
    const [sortedEstimates, setSortedEstimates] = useState<EstimateProjectCustomer[]>(estimates);

    // Sync sortedEstimates with estimates prop whenever it changes (e.g., due to filtering)
    // Also clear selection since the selected estimate may no longer be in the filtered list
    useEffect(() => {
        setSortedEstimates(estimates);
        setSelectedEstimateId(null); // Clear selection when filters change
    }, [estimates]);

    const toggleRow = (estimateId: string) => {
        setExpandedRows((prev) => ({
            ...prev,
            [estimateId]: !prev[estimateId],
        }));
    };

    const clearSelection = () => {
        setSelectedEstimateId(null);
    };

    const handleEdit = () => {
        if (selectedEstimateId) {
            onEdit(selectedEstimateId);
        }
    };

    const handleDelete = () => {
        if (selectedEstimateId) {
            onDelete(selectedEstimateId);
        }
    };

    const handleSendToCustomer = () => {
        if (selectedEstimateId) {
            onSendToCustomer(selectedEstimateId);
        }
    };

    const handleView = () => {
        if (selectedEstimateId) {
            onView(selectedEstimateId);
        }
    };

    const sortByProjectName = (ascending: boolean) => {
        const sorted = [...estimates].sort((a, b) => {
            const nameA = a.project_name.toLowerCase();
            const nameB = b.project_name.toLowerCase();
            return ascending ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
        });
        setSortedEstimates(sorted);
    };

    const sortByCustomerLastName = (ascending: boolean) => {
        const sorted = [...estimates].sort((a, b) => {
            const nameA = a.customer_last_name.toLowerCase();
            const nameB = b.customer_last_name.toLowerCase();
            return ascending ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
        });
        setSortedEstimates(sorted);
    };

    return (
        <div className="space-y-6">
            {/* Sorting Controls */}
            <div className="flex justify-between items-center bg-gray-100 p-3 rounded">
                <div className="flex gap-6 items-center flex-wrap">
                    <div className="flex items-center gap-2">
                        <span className="font-semibold text-foreground">Sort by Project:</span>
                        <button
                            onClick={() => sortByProjectName(true)}
                            className="hover:bg-gray-200 p-1 rounded"
                            title="Sort by Project Name Ascending"
                        >
                            <ChevronUp className="size-4" />
                        </button>
                        <button
                            onClick={() => sortByProjectName(false)}
                            className="hover:bg-gray-200 p-1 rounded"
                            title="Sort by Project Name Descending"
                        >
                            <ChevronDown className="size-4" />
                        </button>
                    </div>
                    <span className="text-gray-400">||</span>
                    <div className="flex items-center gap-2">
                        <span className="font-semibold text-foreground">Sort by Customer Last Name:</span>
                        <button
                            onClick={() => sortByCustomerLastName(true)}
                            className="hover:bg-gray-200 p-1 rounded"
                            title="Sort by Customer Last Name Ascending"
                        >
                            <ChevronUp className="size-4" />
                        </button>
                        <button
                            onClick={() => sortByCustomerLastName(false)}
                            className="hover:bg-gray-200 p-1 rounded"
                            title="Sort by Customer Last Name Descending"
                        >
                            <ChevronDown className="size-4" />
                        </button>
                    </div>
                </div>
            </div>

            {/* Action Bar */}
            <div className="mb-6 p-4 bg-gray-50 rounded-lg flex flex-wrap items-center gap-3 text-sm">
                <div className="flex items-center gap-2">
                    {selectedEstimateId ? (
                        <>
                            <button
                                onClick={clearSelection}
                                className="text-gray-600 hover:text-gray-900"
                                title="Clear Selection"
                            >
                                <X className="size-5" />
                            </button>
                            <Badge variant="default">1 estimate selected</Badge>
                        </>
                    ) : (
                        <span className="text-gray-500">No estimate selected</span>
                    )}
                </div>

                <span className="text-gray-300">|</span>

                <button
                    onClick={handleEdit}
                    disabled={!selectedEstimateId}
                    className={`flex items-center gap-1.5 ${
                        selectedEstimateId
                            ? 'text-primary hover:text-primary-hover cursor-pointer'
                            : 'text-gray-400 cursor-not-allowed'
                    }`}
                    title={selectedEstimateId ? "Edit Estimate" : "Select an estimate to edit"}
                >
                    <Edit className="size-4" /> Edit
                </button>



                <button
                    onClick={handleView}
                    disabled={!selectedEstimateId}
                    className={`flex items-center gap-1.5 ${
                        selectedEstimateId
                            ? 'text-primary hover:text-primary-hover cursor-pointer'
                            : 'text-gray-400 cursor-not-allowed'
                    }`}
                    title={selectedEstimateId ? "View Estimate PDF" : "Select an estimate to view"}
                >
                    <FileText className="size-4" /> PDF View
                </button>

              

                <button
                    onClick={handleSendToCustomer}
                    disabled={!selectedEstimateId}
                    className={`flex items-center gap-1.5 ${
                        selectedEstimateId
                            ? 'text-primary hover:text-primary-hover cursor-pointer'
                            : 'text-gray-400 cursor-not-allowed'
                    }`}
                    title={selectedEstimateId ? "Send Estimate" : "Select an estimate to send"}
                >
                    <Mail className="size-4" /> <MessageSquare className="size-4" /> Send to Customer
                </button>
            </div>

            {/* Estimate List */}
            {sortedEstimates.map((estimate) => (
                <div
                    key={estimate.estimate_id}
                    className="border border-gray-300 rounded p-4 space-y-3"
                >
                    {/* Project and Customer Info */}
                    <div className="ml-6">
                        <div className="mb-2">
                            <strong className="font-semibold text-foreground">Project:</strong>
                            <span className="ml-2 text-sm text-gray-700">
                                {estimate.project_name} - {estimate.project_type} -{' '}
                                {(estimate.project_description || '').length > 75
                                    ? `${estimate.project_description.slice(0, 75)}...`
                                    : estimate.project_description || ''}
                            </span>
                        </div>
                        <div>
                            <strong className="font-semibold text-foreground">Customer:</strong>
                            <span className="ml-2 text-sm text-gray-700">
                                {estimate.customer_first_name} {estimate.customer_last_name},{' '}
                                {formatAddress(
                                    estimate.customer_address_1,
                                    '',
                                    estimate.customer_city,
                                    estimate.customer_state,
                                    estimate.customer_zip
                                )}
                            </span>
                        </div>
                    </div>

                    {/* Estimate Details */}
                    <div className="flex items-start gap-4 bg-gray-100 p-3 rounded">
                        <input
                            type="radio"
                            name="estimateSelection"
                            checked={selectedEstimateId === estimate.estimate_id}
                            onChange={() => setSelectedEstimateId(estimate.estimate_id)}
                            className="mt-1"
                            style={{ accentColor: 'rgb(248, 98, 3)' }}
                        />
                        <button
                            onClick={() => toggleRow(estimate.estimate_id)}
                            className="bg-gray-100 border border-gray-300 hover:bg-gray-200 p-2 rounded-full flex items-center justify-center transition-all w-8 h-8 shrink-0"
                            title={expandedRows[estimate.estimate_id] ? 'Hide Details' : 'Show Details'}
                        >
                            {expandedRows[estimate.estimate_id] ? <ChevronUp className="size-5" /> : <ChevronDown className="size-5" />}
                        </button>
                        <div className="flex-1 text-sm">
                            <div className="space-y-1">
                                <div>
                                    <strong className="text-foreground">Estimate Name:</strong> {estimate.name}
                                </div>
                                <div>
                                    <strong className="text-foreground">Description:</strong> {estimate.description}
                                </div>
                                <div>
                                    <strong className="text-foreground">Terms and Conditions:</strong> {estimate.terms_and_conditions}
                                </div>
                            </div>

                            <div className="mt-3 text-gray-700">
                                <div>
                                    <strong>Total:</strong> ${formatCurrency(Number(estimate.final_total).toFixed(2))}
                                    {' '}= Non-optional: ${formatCurrency(Number(estimate.non_optional_dollar).toFixed(2))}
                                    {' '}+ Optional: ${formatCurrency(Number(estimate.optional_dollar).toFixed(2))}
                                    {' '}- Discount: ${formatCurrency(Number(estimate.discount_usd).toFixed(2))}
                                    {' '}+ Tax: ${formatCurrency(Number(estimate.tax_amount).toFixed(2))} ({Number(estimate.tax_rate).toFixed(2)}%)
                                </div>
                                {(estimate.time_to_complete_day > 0 || estimate.time_to_complete_hr > 0) && (
                                    <div className="mt-1">
                                        <strong>Estimated Time to Complete:</strong>
                                        {' '}
                                        {estimate.time_to_complete_day > 0 && (
                                            <>
                                                {Number(estimate.time_to_complete_day).toFixed(0)}{' '}
                                                {Number(estimate.time_to_complete_day) === 1 ? 'Day' : 'Days'}
                                                {estimate.time_to_complete_hr > 0 && ' and '}
                                            </>
                                        )}
                                        {estimate.time_to_complete_hr > 0 && (
                                            <>
                                                {Number(estimate.time_to_complete_hr).toFixed(0)}{' '}
                                                {Number(estimate.time_to_complete_hr) === 1 ? 'Hour' : 'Hours'}
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Expanded Details - Itemized Estimates */}
                    {expandedRows[estimate.estimate_id] && (
                        <div className="p-2 bg-white">
                            <ItemizedEstimatesView estimateId={estimate.estimate_id} />
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
};

export default EstimateTableView;